import { X } from 'lucide-react-native';
import { useState, useEffect } from 'react';

import { InquiryGuidelines } from '@/components/InquiryGuidelines';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import { LinkText } from '@/components/ui/link';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { segmentClient } from '@/util/analytics';

interface SurgeonAttachmentReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitInquiry: () => void;
  onAttachFiles: () => void;
  expertId?: string;
  hasAttachment?: boolean;
}

export const SurgeonAttachmentReminderModal = ({
  isOpen,
  onClose,
  onSubmitInquiry,
  onAttachFiles,
  expertId,
  hasAttachment,
}: SurgeonAttachmentReminderModalProps) => {
  const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      segmentClient.track(ANALYTICS_EVENTS.SURGEON_ATTACHMENT_REMINDER_SHOWN, {
        expert_id: expertId,
        has_attachments: hasAttachment,
        context: 'inquiry_form',
      });
    }
  }, [isOpen, expertId, hasAttachment]);

  const handleViewGuidelines = () => {
    segmentClient.track(ANALYTICS_EVENTS.INQUIRY_GUIDELINES_OPENED, {
      source: 'surgeon_attachment_reminder',
    });

    onClose();

    setIsGuidelinesOpen(true);
  };

  const handleAttachFiles = () => {
    segmentClient.track(ANALYTICS_EVENTS.SURGEON_ATTACHMENT_REMINDER_ATTACH_CLICKED, {
      expert_id: expertId,
      has_attachments: hasAttachment,
      context: 'inquiry_form',
    });

    onClose();

    onAttachFiles();
  };

  const handleSubmitInquiry = () => {
    segmentClient.track(ANALYTICS_EVENTS.SURGEON_ATTACHMENT_REMINDER_SUBMIT_CLICKED, {
      expert_id: expertId,
      has_attachments: hasAttachment,
      context: 'inquiry_form',
    });

    onClose();

    onSubmitInquiry();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalBackdrop />
        <ModalContent className="p-6">
          <ModalHeader className="pb-4">
            <Heading size="h2-lg" className="font-semibold text-typography-950">
              Important Notice
            </Heading>
            <ModalCloseButton onPress={onClose}>
              <Icon as={X} className="text-background-500" />
            </ModalCloseButton>
          </ModalHeader>

          <ModalBody className="pb-9">
            <VStack className="gap-4">
              <Text className="font-normal text-sm leading-[21px] text-typography-950">
                For surgical inquiries, it's highly recommended to include relevant imaging or
                reports with your submission. This helps surgeons provide accurate, meaningful
                input.
              </Text>

              <Text className="font-normal text-sm leading-[21px] text-typography-950">
                Before uploading, please be sure to remove all patient identifiable information
                (PHI).{' '}
                <LinkText
                  onPress={handleViewGuidelines}
                  size="sm"
                  className="text-info-700 underline data-[hover=true]:text-info-800">
                  View Guidelines
                </LinkText>
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter className="flex-row justify-end gap-2">
            <Button
              variant="outline"
              className="rounded-sm border-primary-300 bg-transparent"
              onPress={handleSubmitInquiry}>
              <ButtonText className="font-medium text-sm text-primary-500">
                Submit inquiry
              </ButtonText>
            </Button>
            <Button className="rounded-sm bg-primary-500" onPress={handleAttachFiles}>
              <ButtonText className="font-medium text-sm text-typography-0">
                Attach files
              </ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <InquiryGuidelines
        isOpen={isGuidelinesOpen}
        onClose={() => setIsGuidelinesOpen(false)}
        context="surgeon_attachment_reminder"
      />
    </>
  );
};
